import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import MainLayout from '../components/MainLayout';
import { useAuth } from '../context/AuthContext';
import { useActiveCompany } from '../hooks/useActiveCompany';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  Building2, Briefcase, Layers, MapPin, Landmark, Users, UserCheck, Hammer,
  Clock, CalendarDays, CalendarX, Wallet, Coins, ReceiptText, HandCoins,
  FileBadge, Building, FileText, Lock, ArrowRight, Sparkles, Info,
  Loader2, Copy as CopyIcon, Wand2, Trash2,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Tile definitions. Each tile maps to a master category and deep-links into /masters
// with the right tab selected. Keep labels short + description one-liner.
const TILES = [
  // Organization (most important — set up first)
  { key: 'legal_entities', collection: 'legal_entities', tab: 'organization', icon: Building2, label: 'Legal Entities', color: 'indigo',
    desc: 'Registered company entities shown on payslips + statutory returns.' },
  { key: 'designations', collection: 'designations', tab: 'organization', icon: Briefcase, label: 'Designations', color: 'blue',
    desc: 'Job titles used in profiles, offer letters and payslip headers.' },
  { key: 'grades', collection: 'grades', tab: 'organization', icon: Layers, label: 'Grades & Bands', color: 'violet',
    desc: 'Pay bands (G1, G2, G3) that validate salary ranges during hiring.' },
  { key: 'branches', collection: 'branches', tab: 'organization', icon: MapPin, label: 'Branches', color: 'cyan',
    desc: 'Office / regional locations for compliance reporting.' },
  { key: 'cost_centers', collection: 'cost_centers', tab: 'organization', icon: Landmark, label: 'Cost Centers', color: 'slate',
    desc: 'Finance codes used to post payroll expense to the GL.' },

  // Employee classification
  { key: 'employee_categories', collection: 'employee_categories', tab: 'employee', icon: Users, label: 'Employee Categories', color: 'emerald',
    desc: 'Regular / Contract / Trainee — drives benefits eligibility.' },
  { key: 'employment_types', collection: 'employment_types', tab: 'employee', icon: UserCheck, label: 'Employment Types', color: 'teal',
    desc: 'Permanent / Contract / Temporary — controls PF/ESI + contract expiry.' },
  { key: 'worker_types', collection: 'worker_types', tab: 'employee', icon: Hammer, label: 'Worker Types', color: 'orange',
    desc: 'Staff / Worker / Executive classification for shift + wage compliance.' },

  // Attendance & Leave
  { key: 'shift_configs', collection: 'shift_configs', tab: 'attendance', icon: Clock, label: 'Shift Master', color: 'sky',
    desc: 'Working hours definition. Drives attendance, OT, and late marks.' },
  { key: 'holiday_calendars', collection: 'holiday_calendars', tab: 'attendance', icon: CalendarDays, label: 'Holiday Calendars', color: 'rose',
    desc: 'Paid holidays list per year. Excluded from LOP calculations.' },
  { key: 'leave_policies', collection: 'leave_types', tab: 'attendance', icon: CalendarX, label: 'Leave Types & Policies', color: 'pink',
    desc: 'Leave types (CL, SL, EL, ML…) with yearly quota, carry-forward and encashment rules.' },

  // Payroll
  { key: 'salary_heads', collection: 'salary_heads', tab: 'payroll', icon: Wallet, label: 'Salary Heads', color: 'green',
    desc: 'Earnings (Basic, HRA) and Deductions (PF, PT) with formulas and tags.' },
  // Salary Structures removed — now managed under Business Controls → Salary Structures (CTC Groups)
  { key: 'reimbursement_heads', collection: 'reimbursement_heads', tab: 'payroll', icon: ReceiptText, label: 'Reimbursement Heads', color: 'yellow',
    desc: 'Claim categories (Fuel, Travel, Internet) with approver matrix.' },
  { key: 'advance_types', collection: 'advance_types', tab: 'payroll', icon: HandCoins, label: 'Advance Types', color: 'amber',
    desc: 'Festival / Medical advances with max-amount + EMI recovery rule.' },

  // Statutory & Misc
  { key: 'statutory_setups', collection: 'statutory_configs', tab: 'statutory', icon: FileBadge, label: 'Statutory Setup', color: 'red',
    desc: 'PF / ESI / PT / TDS rates and registration numbers.' },
  { key: 'banks', collection: 'bank_master', tab: 'statutory', icon: Building, label: 'Bank Master', color: 'fuchsia',
    desc: 'Bank list for salary disbursement + IFSC validation.' },
  { key: 'document_types', collection: 'document_types', tab: 'statutory', icon: FileText, label: 'Document Types', color: 'purple',
    desc: 'Documents to collect during onboarding (Aadhaar, PAN, Passport).' },
];

const COLOR_CLASSES = {
  indigo: 'bg-indigo-50 text-indigo-600 border-indigo-200',
  blue: 'bg-blue-50 text-blue-600 border-blue-200',
  violet: 'bg-violet-50 text-violet-600 border-violet-200',
  cyan: 'bg-cyan-50 text-cyan-600 border-cyan-200',
  slate: 'bg-slate-50 text-slate-600 border-slate-200',
  emerald: 'bg-emerald-50 text-emerald-600 border-emerald-200',
  teal: 'bg-teal-50 text-teal-600 border-teal-200',
  orange: 'bg-orange-50 text-orange-600 border-orange-200',
  sky: 'bg-sky-50 text-sky-600 border-sky-200',
  rose: 'bg-rose-50 text-rose-600 border-rose-200',
  pink: 'bg-pink-50 text-pink-600 border-pink-200',
  green: 'bg-green-50 text-green-600 border-green-200',
  lime: 'bg-lime-50 text-lime-600 border-lime-200',
  yellow: 'bg-yellow-50 text-yellow-600 border-yellow-200',
  amber: 'bg-amber-50 text-amber-600 border-amber-200',
  red: 'bg-red-50 text-red-600 border-red-200',
  fuchsia: 'bg-fuchsia-50 text-fuchsia-600 border-fuchsia-200',
  purple: 'bg-purple-50 text-purple-600 border-purple-200',
};

const TAB_GROUPS = [
  { id: 'organization', label: 'Organization', desc: 'Start here — entities, designations, grades, branches' },
  { id: 'employee', label: 'Employee Classification', desc: 'Category, employment type, worker type' },
  { id: 'attendance', label: 'Attendance & Leave', desc: 'Shifts, holidays, leave policies' },
  { id: 'payroll', label: 'Payroll', desc: 'Salary heads, structures, reimbursements, advances' },
  { id: 'statutory', label: 'Statutory & Misc', desc: 'PF/ESI setup, banks, document types' },
];

const MasterSetup = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useActiveCompany();
  const [counts, setCounts] = useState({});
  const [busy, setBusy] = useState(null); // 'seed' | 'copy' | 'delete'
  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [copySource, setCopySource] = useState('');
  const canEdit = (user?.roles && user.roles.includes('tenant_admin')) || user?.role === 'tenant_admin';
  const activeCompanyObj = companies.find(c => c.id === selectedCompany);

  const fetchCompanies = useCallback(() => axios.get(`${API_URL}/companies`, { withCredentials: true }).then(({ data }) => {
    setCompanies(data);
    setSelectedCompany((cur) => (!cur && data.length > 0 ? data[0].id : cur));
    return data;
  }).catch(() => []), [setSelectedCompany]);

  useEffect(() => { fetchCompanies(); }, [fetchCompanies]);

  const fetchCounts = useCallback(() => {
    if (!selectedCompany) return;
    axios.get(`${API_URL}/masters/all?company_id=${selectedCompany}`, { withCredentials: true })
      .then(({ data }) => {
        const c = {};
        Object.keys(data || {}).forEach(k => { c[k] = (data[k] || []).length; });
        setCounts(c);
      }).catch(() => setCounts({}));
  }, [selectedCompany]);

  useEffect(fetchCounts, [fetchCounts]);

  const totalConfigured = Object.values(counts).reduce((a, n) => a + (n > 0 ? 1 : 0), 0);
  const pct = Math.round((totalConfigured / TILES.length) * 100);
  const goToMaster = (tile) => navigate(`/masters?tab=${tile.tab}&focus=${tile.key}`);

  const handleSeedStarter = async () => {
    if (!activeCompanyObj) return;
    if (!window.confirm(`Seed starter masters for "${activeCompanyObj.name}"?\n\nAdds ~90 common defaults across 12 categories. Safe to run multiple times — existing records are preserved.`)) return;
    setBusy('seed');
    try {
      const { data } = await axios.post(`${API_URL}/companies/${selectedCompany}/seed-masters`, {}, { withCredentials: true });
      toast.success(data.message || 'Starter masters seeded');
      fetchCounts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Seed failed');
    }
    setBusy(null);
  };

  const handleCopyMasters = async () => {
    if (!copySource || copySource === selectedCompany) return;
    setBusy('copy');
    try {
      const { data } = await axios.post(
        `${API_URL}/companies/${selectedCompany}/copy-masters-from/${copySource}`, {},
        { withCredentials: true }
      );
      toast.success(data.message || 'Masters copied');
      setCopyModalOpen(false);
      setCopySource('');
      fetchCounts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Copy failed');
    }
    setBusy(null);
  };

  const handleDeleteCompany = async () => {
    if (!activeCompanyObj) return;
    if (!window.confirm(`PERMANENTLY DELETE company "${activeCompanyObj.name}"?\n\nThis will remove all its masters and configuration. Users assigned to it will have their company cleared.\n\nThis action CANNOT be undone. Proceed?`)) return;
    setBusy('delete');
    try {
      await axios.delete(`${API_URL}/companies/${selectedCompany}`, { withCredentials: true });
      toast.success('Company deleted');
      setSelectedCompany('');
      await fetchCompanies();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
    setBusy(null);
  };

  return (
    <MainLayout>
      <div className="space-y-5" data-testid="master-setup-page">
        {/* Hero with COMPANY SCOPE BANNER */}
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900 flex items-center gap-2">
            <Sparkles className="w-7 h-7 text-[#0052FF]" /> Master Setup
          </h1>
          <p className="mt-1 text-sm text-slate-600">
            Each company in your tenant has its <strong>own independent set of masters</strong>. Configure each one separately, or use "Copy from another company" below to mirror an existing setup.
          </p>
        </div>

        {/* ACTIVE COMPANY BANNER — prominent, sticky feel */}
        <div className="bg-gradient-to-r from-[#0052FF]/5 via-indigo-50 to-purple-50 border-2 border-[#0052FF]/30 rounded-xl p-4" data-testid="active-company-banner">
          <div className="flex flex-wrap items-center gap-4 justify-between">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className="w-12 h-12 rounded-xl bg-[#0052FF] text-white flex items-center justify-center shrink-0 shadow-md">
                <Building2 className="w-6 h-6" />
              </div>
              <div className="min-w-0">
                <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">You are configuring masters for</p>
                <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2 mt-0.5" data-testid="active-company-name">
                  {activeCompanyObj?.name || '—'}
                  {activeCompanyObj?.code && <span className="text-xs font-mono font-medium text-slate-500 bg-slate-100 border border-slate-200 px-2 py-0.5 rounded">{activeCompanyObj.code}</span>}
                </h2>
                <p className="text-xs text-slate-600 mt-0.5">
                  <Info className="inline w-3 h-3 mr-1" />
                  Changes here apply ONLY to this company. Switch the dropdown on the right to configure a different one.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <select
                data-testid="master-setup-company-select"
                value={selectedCompany}
                onChange={e => setSelectedCompany(e.target.value)}
                className="h-10 px-3 bg-white border-2 border-[#0052FF] rounded-md text-sm font-semibold text-[#0052FF] cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#0052FF]/30"
              >
                {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          </div>
        </div>

        {!canEdit && (
          <div className="bg-amber-50 border border-amber-300 rounded-lg px-4 py-3 flex items-center gap-3" data-testid="master-setup-readonly">
            <Lock className="w-5 h-5 text-amber-600 shrink-0" />
            <div>
              <p className="text-sm font-semibold text-amber-900">Read-only view</p>
              <p className="text-xs text-amber-700">Only a Tenant Admin can configure masters. Contact your Tenant Admin.</p>
            </div>
          </div>
        )}

        {/* Quick-action cards: Seed / Copy / Delete */}
        {canEdit && activeCompanyObj && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="master-quick-actions">
            <button
              onClick={handleSeedStarter}
              disabled={busy === 'seed'}
              data-testid="seed-starter-btn"
              className="text-left bg-white border border-slate-200 rounded-xl p-4 hover:border-[#0052FF] hover:shadow-md transition-all disabled:opacity-50 disabled:cursor-wait"
            >
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-emerald-50 border border-emerald-200 flex items-center justify-center shrink-0">
                  {busy === 'seed' ? <Loader2 className="w-4 h-4 animate-spin text-emerald-600" /> : <Wand2 className="w-4 h-4 text-emerald-600" />}
                </div>
                <div>
                  <p className="font-semibold text-sm text-slate-900">Seed Starter Masters</p>
                  <p className="text-xs text-slate-500 mt-0.5">Add ~90 realistic defaults (designations, shifts, salary heads, statutory, leave types, holidays) to this company. Idempotent — won't overwrite your edits.</p>
                </div>
              </div>
            </button>

            <button
              onClick={() => { setCopyModalOpen(true); setCopySource(''); }}
              disabled={companies.length < 2}
              data-testid="copy-masters-btn"
              className="text-left bg-white border border-slate-200 rounded-xl p-4 hover:border-[#0052FF] hover:shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-violet-50 border border-violet-200 flex items-center justify-center shrink-0">
                  <CopyIcon className="w-4 h-4 text-violet-600" />
                </div>
                <div>
                  <p className="font-semibold text-sm text-slate-900">Copy Masters from Another Company</p>
                  <p className="text-xs text-slate-500 mt-0.5">Mirror all masters from a sister company into this one. Only missing records are added — existing ones are preserved.</p>
                </div>
              </div>
            </button>

            <button
              onClick={handleDeleteCompany}
              disabled={busy === 'delete'}
              data-testid="delete-company-btn"
              className="text-left bg-white border border-slate-200 rounded-xl p-4 hover:border-red-400 hover:shadow-md transition-all disabled:opacity-50"
            >
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-red-50 border border-red-200 flex items-center justify-center shrink-0">
                  {busy === 'delete' ? <Loader2 className="w-4 h-4 animate-spin text-red-600" /> : <Trash2 className="w-4 h-4 text-red-600" />}
                </div>
                <div>
                  <p className="font-semibold text-sm text-slate-900">Delete Company</p>
                  <p className="text-xs text-slate-500 mt-0.5">Permanently removes this company and its masters. Blocked if employees are attached.</p>
                </div>
              </div>
            </button>
          </div>
        )}

        {/* Progress card */}
        <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="master-setup-progress">
          <div className="flex items-center justify-between mb-2">
            <div>
              <p className="text-sm font-semibold text-slate-900">Setup progress</p>
              <p className="text-xs text-slate-500">{totalConfigured} of {TILES.length} master categories have at least one record</p>
            </div>
            <p className="text-2xl font-semibold text-[#0052FF] font-mono">{pct}%</p>
          </div>
          <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
            <div className="h-full bg-[#0052FF] transition-all" style={{ width: `${pct}%` }} />
          </div>
        </div>

        {/* Grouped tiles */}
        {TAB_GROUPS.map(grp => {
          const groupTiles = TILES.filter(t => t.tab === grp.id);
          return (
            <div key={grp.id} data-testid={`master-group-${grp.id}`}>
              <div className="mb-3">
                <h2 className="text-lg font-semibold text-slate-900">{grp.label}</h2>
                <p className="text-xs text-slate-500">{grp.desc}</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {groupTiles.map(tile => {
                  const count = counts[tile.collection] || 0;
                  const Icon = tile.icon;
                  const colorCls = COLOR_CLASSES[tile.color] || COLOR_CLASSES.slate;
                  return (
                    <div
                      key={tile.key}
                      data-testid={`master-tile-${tile.key}`}
                      className="group bg-white border border-slate-200 rounded-xl p-5 hover:shadow-md hover:border-slate-300 transition-all flex flex-col"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className={`w-10 h-10 rounded-lg border flex items-center justify-center ${colorCls}`}>
                          <Icon className="w-5 h-5" />
                        </div>
                        <span
                          className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${
                            count > 0 ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'
                          }`}
                        >
                          {count > 0 ? `${count} set` : 'Not set'}
                        </span>
                      </div>
                      <h3 className="font-semibold text-slate-900">{tile.label}</h3>
                      <p className="text-xs text-slate-500 mt-1 flex-1 leading-relaxed">{tile.desc}</p>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => goToMaster(tile)}
                        data-testid={`master-tile-cta-${tile.key}`}
                        className="mt-4 w-full justify-between"
                      >
                        {canEdit ? (count > 0 ? 'Edit / Add more' : 'Set up') : 'View'}
                        <ArrowRight className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* ===== Copy-from modal ===== */}
      {copyModalOpen && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="copy-masters-modal">
          <div className="bg-white rounded-xl w-full max-w-md shadow-2xl">
            <div className="p-5 border-b bg-slate-50 rounded-t-xl">
              <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <CopyIcon className="w-5 h-5 text-violet-600" /> Copy Masters
              </h2>
              <p className="text-xs text-slate-500 mt-0.5">Pick a source company. All its masters will be mirrored into <strong>{activeCompanyObj?.name}</strong>.</p>
            </div>
            <div className="p-5 space-y-3">
              <label className="block text-sm font-semibold text-slate-700">Source company</label>
              <select
                value={copySource}
                onChange={e => setCopySource(e.target.value)}
                data-testid="copy-source-select"
                className="w-full h-11 px-3 bg-white border-2 border-slate-200 rounded-lg text-sm focus:border-[#0052FF] focus:outline-none"
              >
                <option value="">-- select --</option>
                {companies.filter(c => c.id !== selectedCompany).map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
              <p className="text-xs text-slate-500">Existing records in {activeCompanyObj?.name} are preserved; only missing codes are added.</p>
            </div>
            <div className="flex justify-end gap-2 p-5 border-t bg-slate-50 rounded-b-xl">
              <Button variant="outline" onClick={() => setCopyModalOpen(false)} data-testid="copy-cancel">Cancel</Button>
              <Button
                onClick={handleCopyMasters}
                disabled={!copySource || busy === 'copy'}
                data-testid="copy-confirm"
                className="bg-violet-600 hover:bg-violet-700 text-white"
              >
                {busy === 'copy' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CopyIcon className="w-4 h-4 mr-2" />}
                Copy Masters
              </Button>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
};

export default MasterSetup;
